import { useState, useEffect, useMemo } from "react";
import { Button } from "./Button";
import { Modal } from "./Modal";
import { FormField, FormInput, FormLabel } from "./FormFields";
import { IconPicker } from "./IconPicker";
import { TagIcon } from "./TagIcon";
import type { Tag } from "@poi-planner/shared";
import clsx from "clsx";
import { DEFAULT_PHOSPHOR_ICON, toPhosphorTagIcon } from "../utils/tagIcons";
import { tagColorPresets, themeColors } from "../theme/colors";
import { isOwnCategoryTag, useLibraryStore } from "../store/libraryStore";

interface TagEditorModalProps {
  open: boolean;
  onClose: () => void;
  tag?: Tag; // If provided, we're editing; otherwise creating
  onSave: (data: Omit<Tag, "id">) => Promise<string | void>;
  onDelete?: () => Promise<void>;
  usageCount?: number;
}

export function TagEditorModal({
  open,
  onClose,
  tag,
  onSave,
  onDelete,
  usageCount = 0,
}: TagEditorModalProps) {
  const isEditing = !!tag;
  const defaultIconValue = toPhosphorTagIcon(DEFAULT_PHOSPHOR_ICON);

  const [name, setName] = useState("");
  const [icon, setIcon] = useState(defaultIconValue);
  const [color, setColor] = useState<string | undefined>(undefined);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const categoryTags = useLibraryStore((state) => state.categoryTags);
  const setTagCategories = useLibraryStore((state) => state.setTagCategories);
  const ownedCategories = useMemo(
    () =>
      categoryTags
        .filter(isOwnCategoryTag)
        .sort(
          (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
        ),
    [categoryTags],
  );
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [initialCategoryIds, setInitialCategoryIds] = useState<string[]>([]);

  // Reset form when modal opens/tag changes
  useEffect(() => {
    if (open) {
      setName(tag?.name ?? "");
      setIcon(tag?.icon ?? defaultIconValue);
      setColor(tag?.color);
      setShowColorPicker(!!tag?.color);
      setShowDeleteConfirm(false);
      setError(null);
      const initial = tag
        ? ownedCategories
            .filter((category) => category.tagIds.includes(tag.id))
            .map((category) => category.id)
        : [];
      setSelectedCategoryIds(initial);
      setInitialCategoryIds(initial);
    }
  }, [open, tag, defaultIconValue, ownedCategories]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("Name is required");
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const createdTagId = await onSave({
        name: name.trim(),
        icon,
        color: showColorPicker ? color : undefined,
      });
      const savedTagId = tag?.id ?? createdTagId;
      const categoriesChanged =
        [...selectedCategoryIds].sort().join(",") !==
        [...initialCategoryIds].sort().join(",");
      if (savedTagId && categoriesChanged) {
        await setTagCategories(savedTagId, selectedCategoryIds);
      }
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!onDelete) return;

    setSaving(true);
    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEditing ? "Edit Tag" : "New Tag"}
      icon="label"
      size="lg"
      footer={
        <div className="flex items-center justify-between w-full">
          <div>
            {isEditing && onDelete && (
              <Button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={saving}
                variant="danger"
              >
                Delete Tag
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            <Button type="button" onClick={onClose} variant="secondary">
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={saving || !name.trim()}
              variant="primary"
              loading={saving}
            >
              {saving ? "Saving..." : isEditing ? "Save Changes" : "Create Tag"}
            </Button>
          </div>
        </div>
      }
    >
      {/* Delete confirmation overlay */}
      {showDeleteConfirm && (
        <div className="absolute inset-0 bg-white/95 backdrop-blur-sm z-10 flex flex-col items-center justify-center p-6">
          <div className="flex items-center justify-center size-16 rounded-full bg-error-bg-strong mb-4">
            <span className="material-symbols-outlined text-3xl text-error">
              delete_forever
            </span>
          </div>
          <h3 className="text-lg font-bold text-primary mb-2">
            Delete "{tag?.name}"?
          </h3>
          <p className="text-sm text-secondary text-center mb-6 max-w-xs">
            {usageCount > 0
              ? `This tag is used by ${usageCount} ${usageCount === 1 ? "POI" : "POIs"}. Deleting it will remove the tag from all POIs.`
              : "This action cannot be undone."}
          </p>
          <div className="flex gap-3">
            <Button
              type="button"
              onClick={() => setShowDeleteConfirm(false)}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleDelete}
              disabled={saving}
              variant="danger"
              loading={saving}
            >
              {saving ? "Deleting..." : "Delete Tag"}
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-col gap-5 relative">
        {/* Error message */}
        {error && (
          <div className="flex items-center gap-2 p-3 bg-error-bg border border-error/25 rounded-lg text-sm text-error">
            <span className="material-symbols-outlined text-lg">error</span>
            {error}
          </div>
        )}

        {/* Preview */}
        <div className="flex items-center gap-4 p-4 bg-subtle rounded-xl">
          <div
            className="flex items-center justify-center size-14 rounded-xl transition-colors"
            style={{
              backgroundColor:
                showColorPicker && color ? color : themeColors.primary,
            }}
          >
            <TagIcon icon={icon} size={30} whiteTint />
          </div>
          <div className="flex-1">
            <div className="text-sm font-bold text-primary">
              {name || "Tag Name"}
            </div>
            <div className="text-xs text-secondary mt-0.5">
              Preview of your tag icon
            </div>
          </div>
          {isEditing && usageCount > 0 && (
            <div className="px-2.5 py-1 bg-white rounded-lg text-xs font-semibold text-secondary border border-border">
              {usageCount} {usageCount === 1 ? "POI" : "POIs"}
            </div>
          )}
        </div>

        {/* Name input */}
        <FormField label="Name">
          <FormInput
            value={name}
            onChange={setName}
            placeholder="e.g., Restaurant, Museum, Park"
          />
        </FormField>

        <div>
          <FormLabel>Categories</FormLabel>
          <p className="mb-2 text-xs text-tertiary">
            Choose broad filters for this tag. A tag can appear in more than one
            category.
          </p>
          <div className="flex flex-wrap gap-2">
            {ownedCategories.map((category) => {
              const selected = selectedCategoryIds.includes(category.id);
              return (
                <Button
                  key={category.id}
                  type="button"
                  variant="unstyled"
                  selected={selected}
                  onClick={() =>
                    setSelectedCategoryIds((current) =>
                      selected
                        ? current.filter((id) => id !== category.id)
                        : [...current, category.id],
                    )
                  }
                  className={clsx(
                    "inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-semibold transition-colors",
                    selected
                      ? "border-primary bg-primary text-white"
                      : "border-border bg-white text-secondary hover:bg-subtle",
                  )}
                >
                  <span
                    className="material-symbols-outlined text-sm"
                    aria-hidden="true"
                  >
                    account_tree
                  </span>
                  {category.name}
                </Button>
              );
            })}
          </div>
        </div>

        {/* Icon picker */}
        <div>
          <FormLabel>Icon</FormLabel>
          <IconPicker
            value={icon}
            onChange={setIcon}
            color={showColorPicker ? color : undefined}
          />
        </div>

        {/* Color picker */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <FormLabel className="!mb-0">Marker Color</FormLabel>
            <button
              type="button"
              onClick={() => setShowColorPicker(!showColorPicker)}
              className={clsx(
                "relative w-9 h-5 rounded-full border-none cursor-pointer transition-colors p-0",
                showColorPicker ? "bg-primary" : "bg-border",
              )}
            >
              <div
                className={clsx(
                  "absolute top-0.5 size-4 rounded-full bg-white transition-[left]",
                  showColorPicker ? "left-[18px]" : "left-0.5",
                )}
              />
            </button>
          </div>

          {showColorPicker && (
            <div className="flex gap-2 mt-2">
              {tagColorPresets.map((c) => (
                <button
                  key={c.color}
                  type="button"
                  onClick={() => setColor(c.color)}
                  title={c.label}
                  className={clsx(
                    "size-9 rounded-lg cursor-pointer transition-all hover:scale-110 border-2",
                    color === c.color
                      ? "ring-2 ring-offset-2 ring-primary"
                      : "border-transparent",
                  )}
                  style={{ backgroundColor: c.color }}
                />
              ))}
            </div>
          )}
          {!showColorPicker && (
            <p className="text-xs text-tertiary mt-1">
              Uses default dark marker. Enable to customize color.
            </p>
          )}
        </div>
      </div>
    </Modal>
  );
}
